import { useSyncExternalStore } from "react";
import {
  getLastParentOrigin,
  isAllowedOrigin,
  listen,
  send,
  type SessionContext,
} from "@/iframe/postMessage";

export type SessionStatus =
  | "idle"
  | "waitingHello"
  | "ready"
  | "denied"
  | "standaloneSynthetic";

export type IframeSessionSnapshot = {
  status: SessionStatus;
  sessionContext: SessionContext;
  parentOrigin: string | null;
};

const SYNTHETIC_CONTEXT: SessionContext = {
  repId: "DEMO-REP",
  accountRef: "00447-190288-15",
  tenantId: "demo",
  __synthetic: true,
};

let snapshot: IframeSessionSnapshot = {
  status: "idle",
  sessionContext: SYNTHETIC_CONTEXT,
  parentOrigin: null,
};

const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function setSnapshot(next: Partial<IframeSessionSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  emit();
}

export function getIframeSessionSnapshot(): IframeSessionSnapshot {
  return snapshot;
}

export function useIframeSession(): IframeSessionSnapshot {
  return useSyncExternalStore(
    (onStoreChange) => {
      listeners.add(onStoreChange);
      return () => listeners.delete(onStoreChange);
    },
    getIframeSessionSnapshot,
    getIframeSessionSnapshot,
  );
}

let initialized = false;

export function initializeIframeSession(): void {
  if (initialized) return;
  initialized = true;

  if (window.parent === window) {
    setSnapshot({ status: "standaloneSynthetic", sessionContext: SYNTHETIC_CONTEXT, parentOrigin: null });
    return;
  }

  setSnapshot({ status: "waitingHello" });

  listen((msg, origin) => {
    if (!isAllowedOrigin(origin)) {
      setSnapshot({ status: "denied" });
      return;
    }
    setSnapshot({
      status: "ready",
      sessionContext: msg.payload.sessionContext,
      parentOrigin: origin,
    });
    try {
      send(window.parent, { version: 1, type: "account-mgt:ready", payload: {} }, origin);
    } catch (err) {
      console.warn("iframe session: ready post failed", err);
    }
  });
}

export function postToParent(msg: Parameters<typeof send>[1]): void {
  if (window.parent === window) return;
  const origin = getLastParentOrigin() ?? snapshot.parentOrigin;
  if (!origin) return;
  try {
    send(window.parent, msg, origin);
  } catch (err) {
    console.warn("postToParent failed", err);
  }
}
